import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useResponsive } from '../hooks/useResponsive';
import { COLORS, SPACING, FONT_SIZE, BORDER_RADIUS, SHADOWS } from '../constants/theme';

const DrillRoutineManager = ({ profile }) => {
  const { isDesktop } = useResponsive();
  const [routines, setRoutines] = useState([]);
  const [drills, setDrills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingRoutine, setEditingRoutine] = useState(null);
  const [viewingRoutine, setViewingRoutine] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
  });

  const [routineItems, setRoutineItems] = useState([]); // Drills added to current routine

  useEffect(() => {
    loadRoutines();
    loadDrills();
  }, []);

  const loadRoutines = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('drill_templates')
        .select(
          `
          *,
          created_by_profile:profiles!drill_templates_created_by_fkey(full_name),
          template_items:drill_template_items(count)
        `
        )
        .eq('organization_id', profile.organization_id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setRoutines(data || []);
    } catch (err) {
      console.error('Error loading routines:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDrills = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('drills')
        .select('id, name, description, category, difficulty_level, duration_minutes')
        .order('name');

      if (fetchError) throw fetchError;

      setDrills(data || []);
    } catch (err) {
      console.error('Error loading drills:', err);
      setError(err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name.trim()) {
      setError('Routine name is required');
      return;
    }

    if (routineItems.length === 0) {
      setError('Please add at least one drill to the routine');
      return;
    }

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const routineData = {
        organization_id: profile.organization_id,
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        is_active: formData.is_active,
        created_by: user.id,
      };

      if (editingRoutine) {
        // Update existing routine
        const { error: updateError } = await supabase
          .from('drill_templates')
          .update(routineData)
          .eq('id', editingRoutine);

        if (updateError) throw updateError;

        // Delete existing items and recreate them
        const { error: deleteError } = await supabase
          .from('drill_template_items')
          .delete()
          .eq('template_id', editingRoutine);

        if (deleteError) throw deleteError;

        // Insert new items
        const { error: insertError } = await supabase.from('drill_template_items').insert(
          routineItems.map((item, index) => ({
            template_id: editingRoutine,
            drill_id: item.drill_id,
            sets: item.sets,
            reps: item.reps,
            difficulty_level: item.difficulty_level,
            coach_notes: item.coach_notes || null,
            order_index: index,
          }))
        );

        if (insertError) throw insertError;

        setSuccess('Routine updated successfully!');
      } else {
        // Create new routine
        const { data: newRoutine, error: insertError } = await supabase
          .from('drill_templates')
          .insert(routineData)
          .select()
          .single();

        if (insertError) throw insertError;

        // Insert routine items
        const { error: itemsError } = await supabase.from('drill_template_items').insert(
          routineItems.map((item, index) => ({
            template_id: newRoutine.id,
            drill_id: item.drill_id,
            sets: item.sets,
            reps: item.reps,
            difficulty_level: item.difficulty_level,
            coach_notes: item.coach_notes || null,
            order_index: index,
          }))
        );

        if (itemsError) throw itemsError;

        setSuccess('Routine created successfully!');
      }

      setShowCreateForm(false);
      setEditingRoutine(null);
      loadRoutines();

      // Reset form
      setFormData({
        name: '',
        description: '',
        is_active: true,
      });
      setRoutineItems([]);
    } catch (err) {
      console.error('Error saving routine:', err);
      if (err.message.includes('duplicate')) {
        setError('A routine with this name already exists in your organization');
      } else {
        setError(err.message);
      }
    }
  };

  const handleEdit = async (routine) => {
    setEditingRoutine(routine.id);
    setFormData({
      name: routine.name,
      description: routine.description || '',
      is_active: routine.is_active,
    });

    // Load routine items
    try {
      const { data, error } = await supabase
        .from('drill_template_items')
        .select(
          `
          *,
          drill:drills(id, name, description, duration_minutes, category, difficulty_level)
        `
        )
        .eq('template_id', routine.id)
        .order('order_index');

      if (error) throw error;

      setRoutineItems(
        data.map((item) => ({
          drill_id: item.drill_id,
          drill_name: item.drill.name,
          drill_duration_minutes: item.drill.duration_minutes,
          sets: item.sets,
          reps: item.reps,
          difficulty_level: item.difficulty_level,
          coach_notes: item.coach_notes || '',
        }))
      );
    } catch (err) {
      console.error('Error loading routine items:', err);
      setError(err.message);
    }

    setShowCreateForm(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (routineId) => {
    if (
      !window.confirm('Are you sure you want to delete this routine? This action cannot be undone.')
    ) {
      return;
    }

    try {
      const { error: deleteError } = await supabase
        .from('drill_templates')
        .delete()
        .eq('id', routineId);

      if (deleteError) throw deleteError;

      setSuccess('Routine deleted successfully!');
      loadRoutines();
    } catch (err) {
      console.error('Error deleting routine:', err);
      setError(err.message);
    }
  };

  const handleToggleActive = async (routine) => {
    try {
      const { error } = await supabase
        .from('drill_templates')
        .update({ is_active: !routine.is_active })
        .eq('id', routine.id);

      if (error) throw error;

      setSuccess(`Routine ${!routine.is_active ? 'activated' : 'deactivated'} successfully!`);
      loadRoutines();
    } catch (err) {
      console.error('Error toggling routine:', err);
      setError(err.message);
    }
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingRoutine(null);
    setFormData({
      name: '',
      description: '',
      is_active: true,
    });
    setRoutineItems([]);
    setError('');
  };

  const handleAddDrill = () => {
    const drillSelect = document.getElementById('drill-select');
    const setsInput = document.getElementById('drill-sets');
    const repsInput = document.getElementById('drill-reps');
    const difficultySelect = document.getElementById('drill-difficulty');
    const notesInput = document.getElementById('drill-notes');

    const drillId = drillSelect.value;
    const sets = parseInt(setsInput.value);
    const reps = parseInt(repsInput.value);
    const difficultyLevel = difficultySelect.value;
    const coachNotes = notesInput.value;

    if (!drillId) {
      setError('Please select a drill');
      return;
    }

    // Check if drill already in routine
    if (routineItems.some((item) => item.drill_id === drillId)) {
      setError('This drill is already in the routine');
      return;
    }

    const drill = drills.find((d) => d.id === drillId);
    if (!drill) return;

    setRoutineItems([
      ...routineItems,
      {
        drill_id: drillId,
        drill_name: drill.name,
        drill_duration_minutes: drill.duration_minutes,
        sets,
        reps,
        difficulty_level: difficultyLevel,
        coach_notes: coachNotes,
      },
    ]);

    // Reset form fields
    drillSelect.value = '';
    setsInput.value = '3';
    repsInput.value = '10';
    difficultySelect.value = 'intermediate';
    notesInput.value = '';
    setError('');
  };

  const handleRemoveDrill = (index) => {
    setRoutineItems(routineItems.filter((_, i) => i !== index));
  };

  const handleMoveDrill = (index, direction) => {
    const newItems = [...routineItems];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newItems.length) return;

    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    setRoutineItems(newItems);
  };

  const calculateTotalDuration = () => {
    return routineItems.reduce((total, item) => total + (item.drill_duration_minutes || 0), 0);
  };

  const getItemCount = (routine) => {
    return routine.template_items?.[0]?.count || 0;
  };

  const handleViewRoutine = async (routine) => {
    try {
      const { data, error } = await supabase
        .from('drill_template_items')
        .select(
          `
          *,
          drill:drills(name, description, duration_minutes, category, difficulty_level)
        `
        )
        .eq('template_id', routine.id)
        .order('order_index');

      if (error) throw error;

      setViewingRoutine({ ...routine, items: data });
    } catch (err) {
      console.error('Error loading routine details:', err);
      setError(err.message);
    }
  };

  // Styles
  const containerStyle = {
    padding: SPACING.lg,
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: SPACING.lg,
    flexWrap: 'wrap',
    gap: SPACING.md,
  };

  const buttonStyle = {
    padding: `${SPACING.md} ${SPACING.lg}`,
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    border: 'none',
    borderRadius: BORDER_RADIUS.sm,
    cursor: 'pointer',
    fontSize: FONT_SIZE.base,
    fontWeight: '500',
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: COLORS.gray[400],
  };

  const successButtonStyle = {
    ...buttonStyle,
    backgroundColor: COLORS.success,
  };

  const dangerButtonStyle = {
    ...buttonStyle,
    backgroundColor: COLORS.danger,
  };

  const formStyle = {
    backgroundColor: COLORS.background.card,
    padding: SPACING.lg,
    borderRadius: BORDER_RADIUS.md,
    boxShadow: SHADOWS.md,
    marginBottom: SPACING.lg,
  };

  const inputGroupStyle = {
    marginBottom: SPACING.md,
  };

  const labelStyle = {
    display: 'block',
    marginBottom: SPACING.sm,
    fontSize: FONT_SIZE.base,
    fontWeight: '500',
    color: COLORS.gray[700],
  };

  const inputStyle = {
    width: '100%',
    padding: SPACING.md,
    fontSize: FONT_SIZE.base,
    border: `1px solid ${COLORS.gray[400]}`,
    borderRadius: BORDER_RADIUS.sm,
    boxSizing: 'border-box',
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: COLORS.white,
    boxShadow: SHADOWS.sm,
  };

  const thStyle = {
    padding: SPACING.md,
    textAlign: 'left',
    borderBottom: `2px solid ${COLORS.gray[300]}`,
    fontSize: FONT_SIZE.base,
    fontWeight: '600',
    color: COLORS.gray[700],
  };

  const tdStyle = {
    padding: SPACING.md,
    borderBottom: `1px solid ${COLORS.gray[300]}`,
    fontSize: FONT_SIZE.base,
  };

  const cardStyle = {
    backgroundColor: COLORS.white,
    padding: SPACING.lg,
    borderRadius: BORDER_RADIUS.md,
    boxShadow: SHADOWS.sm,
    marginBottom: SPACING.md,
  };

  const alertStyle = (type) => ({
    padding: SPACING.md,
    borderRadius: BORDER_RADIUS.sm,
    marginBottom: SPACING.lg,
    backgroundColor: type === 'error' ? COLORS.alert.error.bg : COLORS.alert.success.bg,
    color: type === 'error' ? COLORS.alert.error.text : COLORS.alert.success.text,
    border: `1px solid ${type === 'error' ? COLORS.alert.error.border : COLORS.alert.success.border}`,
  });

  const badgeStyle = (isActive) => ({
    display: 'inline-block',
    padding: `${SPACING.xs} ${SPACING.sm}`,
    backgroundColor: isActive ? COLORS.success : COLORS.gray[400],
    color: COLORS.white,
    borderRadius: BORDER_RADIUS.sm,
    fontSize: FONT_SIZE.sm,
    fontWeight: '500',
  });

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h2 style={{ margin: 0, fontSize: FONT_SIZE.xl }}>Drill Routines</h2>
        <button
          onClick={() => {
            setShowCreateForm(!showCreateForm);
            setEditingRoutine(null);
            setError('');
          }}
          style={buttonStyle}
        >
          {showCreateForm ? 'Cancel' : '+ Create Routine'}
        </button>
      </div>

      {error && <div style={alertStyle('error')}>{error}</div>}
      {success && <div style={alertStyle('success')}>{success}</div>}

      {showCreateForm && (
        <div style={formStyle}>
          <h3 style={{ marginTop: 0, fontSize: FONT_SIZE.lg }}>
            {editingRoutine ? 'Edit Routine' : 'Create New Routine'}
          </h3>

          <form onSubmit={handleSubmit}>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Routine Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={inputStyle}
                placeholder="e.g., The Basics, Weekly Hitting Package"
                required
              />
            </div>

            <div style={inputGroupStyle}>
              <label style={labelStyle}>Description (Optional)</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                placeholder="Describe what this routine includes..."
              />
            </div>

            {/* Add Drill Section */}
            <div style={{ ...cardStyle, backgroundColor: COLORS.background.page }}>
              <h4 style={{ marginTop: 0, fontSize: FONT_SIZE.base }}>Add Drills to Routine</h4>
              {drills.length === 0 ? (
                <div style={{ padding: SPACING.md, textAlign: 'center', color: COLORS.gray[600] }}>
                  <p>No drills available. Please create drills first in the Drill Library tab.</p>
                  <button
                    type="button"
                    onClick={() => (window.location.hash = '#drills')}
                    style={{
                      ...buttonStyle,
                      marginTop: SPACING.md,
                    }}
                  >
                    Go to Drill Library
                  </button>
                </div>
              ) : (
                <>
                  <div id="add-drill-form">
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: isDesktop ? '2fr 1fr 1fr 1fr 2fr auto' : '1fr',
                        gap: SPACING.sm,
                      }}
                    >
                      <div>
                        <label style={labelStyle}>Drill</label>
                        <select id="drill-select" name="drill" style={inputStyle}>
                          <option value="">Select drill...</option>
                          {drills.map((drill) => (
                            <option key={drill.id} value={drill.id}>
                              {drill.name} ({drill.duration_minutes || 0} min)
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={labelStyle}>Sets</label>
                        <input
                          id="drill-sets"
                          name="sets"
                          type="number"
                          defaultValue="3"
                          min="1"
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Reps</label>
                        <input
                          id="drill-reps"
                          name="reps"
                          type="number"
                          defaultValue="10"
                          min="1"
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Difficulty</label>
                        <select
                          id="drill-difficulty"
                          name="difficulty"
                          style={inputStyle}
                          defaultValue="intermediate"
                        >
                          <option value="beginner">Beginner</option>
                          <option value="intermediate">Intermediate</option>
                          <option value="advanced">Advanced</option>
                        </select>
                      </div>
                      <div>
                        <label style={labelStyle}>Notes</label>
                        <input
                          id="drill-notes"
                          name="notes"
                          type="text"
                          placeholder="Optional notes..."
                          style={inputStyle}
                        />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={handleAddDrill}
                          style={{ ...buttonStyle, padding: SPACING.md }}
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Routine Items List */}
                  {routineItems.length > 0 && (
                    <div style={{ marginTop: SPACING.lg }}>
                      <h4 style={{ fontSize: FONT_SIZE.base, marginBottom: SPACING.md }}>
                        Routine Drills ({routineItems.length}) - Total: {calculateTotalDuration()}{' '}
                        minutes
                      </h4>
                      <table style={tableStyle}>
                        <thead>
                          <tr>
                            <th style={thStyle}>#</th>
                            <th style={thStyle}>Drill</th>
                            <th style={thStyle}>Sets</th>
                            <th style={thStyle}>Reps</th>
                            <th style={thStyle}>Difficulty</th>
                            <th style={thStyle}>Duration</th>
                            <th style={thStyle}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {routineItems.map((item, index) => (
                            <tr key={index}>
                              <td style={tdStyle}>{index + 1}</td>
                              <td style={tdStyle}>
                                {item.drill_name}
                                {item.coach_notes && (
                                  <div
                                    style={{
                                      fontSize: FONT_SIZE.sm,
                                      color: COLORS.gray[600],
                                      marginTop: SPACING.xs,
                                    }}
                                  >
                                    Note: {item.coach_notes}
                                  </div>
                                )}
                              </td>
                              <td style={tdStyle}>{item.sets}</td>
                              <td style={tdStyle}>{item.reps}</td>
                              <td style={tdStyle}>{item.difficulty_level}</td>
                              <td style={tdStyle}>{item.drill_duration_minutes || 0} min</td>
                              <td style={tdStyle}>
                                <div style={{ display: 'flex', gap: SPACING.xs }}>
                                  <button
                                    onClick={() => handleMoveDrill(index, 'up')}
                                    disabled={index === 0}
                                    style={{
                                      ...secondaryButtonStyle,
                                      padding: `${SPACING.xs} ${SPACING.sm}`,
                                      opacity: index === 0 ? 0.5 : 1,
                                    }}
                                  >
                                    ↑
                                  </button>
                                  <button
                                    onClick={() => handleMoveDrill(index, 'down')}
                                    disabled={index === routineItems.length - 1}
                                    style={{
                                      ...secondaryButtonStyle,
                                      padding: `${SPACING.xs} ${SPACING.sm}`,
                                      opacity: index === routineItems.length - 1 ? 0.5 : 1,
                                    }}
                                  >
                                    ↓
                                  </button>
                                  <button
                                    onClick={() => handleRemoveDrill(index)}
                                    style={{
                                      ...dangerButtonStyle,
                                      padding: `${SPACING.xs} ${SPACING.sm}`,
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>

            <div style={{ display: 'flex', gap: SPACING.md, marginTop: SPACING.lg }}>
              <button type="submit" style={buttonStyle} disabled={routineItems.length === 0}>
                {editingRoutine ? 'Update Routine' : 'Create Routine'}
              </button>
              <button type="button" onClick={handleCancel} style={secondaryButtonStyle}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* View Routine Modal */}
      {viewingRoutine && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: SPACING.md,
          }}
          onClick={() => setViewingRoutine(null)}
        >
          <div
            style={{
              backgroundColor: COLORS.white,
              borderRadius: BORDER_RADIUS.md,
              padding: SPACING.lg,
              maxWidth: '800px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: SHADOWS.xl,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>{viewingRoutine.name}</h3>
            {viewingRoutine.description && <p>{viewingRoutine.description}</p>}
            <p>
              <strong>Total Duration:</strong>{' '}
              {viewingRoutine.items.reduce(
                (sum, item) => sum + (item.drill.duration_minutes || 0),
                0
              )}{' '}
              minutes
            </p>

            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>Drill</th>
                  <th style={thStyle}>Sets × Reps</th>
                  <th style={thStyle}>Difficulty</th>
                  <th style={thStyle}>Duration</th>
                </tr>
              </thead>
              <tbody>
                {viewingRoutine.items.map((item, index) => (
                  <tr key={item.id}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {item.drill.name}
                      {item.coach_notes && (
                        <div
                          style={{
                            fontSize: FONT_SIZE.sm,
                            color: COLORS.gray[600],
                            marginTop: SPACING.xs,
                          }}
                        >
                          Note: {item.coach_notes}
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {item.sets} × {item.reps}
                    </td>
                    <td style={tdStyle}>{item.difficulty_level}</td>
                    <td style={tdStyle}>{item.drill.duration_minutes || 0} min</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ marginTop: SPACING.lg }}>
              <button onClick={() => setViewingRoutine(null)} style={buttonStyle}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading routines...</p>
      ) : routines.length === 0 ? (
        <p style={{ color: COLORS.gray[500] }}>No routines yet. Create one to get started!</p>
      ) : isDesktop ? (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Description</th>
              <th style={thStyle}>Drills</th>
              <th style={thStyle}>Created By</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {routines.map((routine) => (
              <tr key={routine.id}>
                <td style={tdStyle}>
                  <strong>{routine.name}</strong>
                </td>
                <td style={tdStyle}>{routine.description || '-'}</td>
                <td style={tdStyle}>{getItemCount(routine)}</td>
                <td style={tdStyle}>{routine.created_by_profile?.full_name || 'Unknown'}</td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', gap: SPACING.sm, flexWrap: 'wrap' }}>
                    <button
                      onClick={() => handleViewRoutine(routine)}
                      style={{ ...buttonStyle, padding: `${SPACING.sm} ${SPACING.md}` }}
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleEdit(routine)}
                      style={{ ...secondaryButtonStyle, padding: `${SPACING.sm} ${SPACING.md}` }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(routine.id)}
                      style={{ ...dangerButtonStyle, padding: `${SPACING.sm} ${SPACING.md}` }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        // Mobile card view
        <div>
          {routines.map((routine) => (
            <div key={routine.id} style={cardStyle}>
              <h3 style={{ margin: 0, marginBottom: SPACING.sm, fontSize: FONT_SIZE.lg }}>
                {routine.name}
              </h3>
              {routine.description && (
                <p style={{ margin: `${SPACING.xs} 0`, color: COLORS.gray[600] }}>
                  {routine.description}
                </p>
              )}
              <p style={{ margin: `${SPACING.xs} 0` }}>
                <strong>Drills:</strong> {getItemCount(routine)}
              </p>
              <p style={{ margin: `${SPACING.xs} 0`, color: COLORS.gray[600] }}>
                <strong>Created By:</strong> {routine.created_by_profile?.full_name || 'Unknown'}
              </p>
              <div
                style={{
                  display: 'flex',
                  gap: SPACING.sm,
                  marginTop: SPACING.md,
                  flexWrap: 'wrap',
                }}
              >
                <button
                  onClick={() => handleViewRoutine(routine)}
                  style={{ ...buttonStyle, flex: 1 }}
                >
                  View
                </button>
                <button
                  onClick={() => handleEdit(routine)}
                  style={{ ...secondaryButtonStyle, flex: 1 }}
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(routine.id)}
                  style={{ ...dangerButtonStyle, flex: 1 }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DrillRoutineManager;
